import { useState, useMemo, useContext } from "react";
import { Plus, ArrowLeft, Send, RefreshCw, Info } from "lucide-react";
import { ulid } from "ulid";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { trpc } from "../../../trpc";
import UserContext from "../../../contexts/UserContext";
import type { OptimisticMessage } from "./composer/useOptimisticMessages";
import type { ComposerLeadData } from "./composer/MessageComposer";

type TemplateFlowView = "select" | "create" | "fill-params";

type SelectedTemplate = {
  id: string;
  name: string;
  bodyText: string;
  language: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  params: string[];
};

type Props = {
  merchantId: string;
  leadId: string;
  leadData?: ComposerLeadData;
  wabaId?: string;
  whatsappConnectionId?: string;
  onTemplateSent: () => void;
  onOptimisticMessage: (message: OptimisticMessage) => void;
  onRemoveOptimisticMessage: (id: string) => void;
  onMarkOptimisticFailed: (id: string) => void;
};

const getAutoFillValue = (paramName: string, leadData?: ComposerLeadData): string => {
  const normalized = paramName.toLowerCase();
  if (["name", "nombre", "customer", "cliente"].includes(normalized)) return leadData?.name || "";
  if (["phone", "telefono", "celular"].includes(normalized)) return leadData?.phone || "";
  if (["email", "correo"].includes(normalized)) return leadData?.email || "";
  return "";
};

const renderBodyWithHighlightedParams = (bodyText: string, values: Record<string, string>) => {
  const parts = bodyText.split(/({{(\w+)}})/g);
  const rendered: JSX.Element[] = [];
  let i = 0;

  while (i < parts.length) {
    const part = parts[i];
    // Check if this is a full match like {{param}}
    if (part.match(/^{{\w+}}$/)) {
      const paramName = parts[i + 1]; // The capture group with just the param name
      const value = values[paramName];
      rendered.push(
        <span key={i} className={value ? "font-bold text-green-600" : "font-bold text-orange-500"}>
          {value || part}
        </span>
      );
      i += 2; // Skip the inner capture group
    } else {
      if (part) {
        rendered.push(<span key={i}>{part}</span>);
      }
      i += 1;
    }
  }

  return <p className="whitespace-pre-wrap text-sm">{rendered}</p>;
};

const TemplateMessageFlow = ({
  merchantId,
  leadId,
  leadData,
  wabaId,
  whatsappConnectionId,
  onTemplateSent,
  onOptimisticMessage,
  onRemoveOptimisticMessage,
  onMarkOptimisticFailed,
}: Props) => {
  const { email: userEmail } = useContext(UserContext);
  const isMobile = useIsMobile();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [view, setView] = useState<TemplateFlowView>("select");
  const [selectedTemplate, setSelectedTemplate] = useState<SelectedTemplate | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const utils = trpc.useUtils();

  // Create form state
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("es");
  const [bodyText, setBodyText] = useState("");
  const [paramExamples, setParamExamples] = useState<Record<string, string>>({});

  const { data, isLoading } = trpc.whatsappTemplates.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );

  const sendTemplateMutation = trpc.conversations.sendTemplateMessage.useMutation();
  const createTemplateMutation = trpc.whatsappTemplates.create.useMutation();
  const syncStatusesMutation = trpc.whatsappTemplates.syncStatuses.useMutation({
    onSuccess: (data) => {
      utils.whatsappTemplates.list.invalidate();
      if (data.errors.length > 0) {
        toast.warning("Sincronización completada", {
          description: data.message,
          duration: 3000,
        });
      } else {
        toast.success("Sincronización completada", {
          description: data.message,
          duration: 3000,
        });
      }
    },
    onError: (error) => {
      toast.error("Error al sincronizar", {
        description: error.message,
        duration: 5000,
      });
    },
  });

  const templates = useMemo(() => {
    if (!data?.templates || !wabaId) return [];
    return data.templates.filter(
      (template) =>
        template.wabaId === wabaId &&
        template.config.category !== "AUTHENTICATION" &&
        template.bodyPreview
    );
  }, [data?.templates, wabaId]);

  const approvedTemplates = useMemo(
    () => templates.filter((t) => t.status === "APPROVED"),
    [templates]
  );

  const nonApprovedTemplates = useMemo(
    () => templates.filter((t) => t.status !== "APPROVED"),
    [templates]
  );

  const extractParams = (bodyText: string): string[] => {
    const matches = bodyText.matchAll(/{{(\w+)}}/g);
    return [...matches].map((m) => m[1]);
  };

  const handleSelectTemplate = (template: (typeof templates)[number]) => {
    if (template.status !== "APPROVED") return;

    const bodyText = template.bodyPreview || "";
    const params = extractParams(bodyText);

    const selected: SelectedTemplate = {
      id: template.id,
      name: template.name,
      bodyText,
      language: template.language,
      status: template.status,
      params,
    };

    setSelectedTemplate(selected);

    const autoFilled: Record<string, string> = {};
    params.forEach((param) => {
      autoFilled[param] = getAutoFillValue(param, leadData);
    });
    setParamValues(autoFilled);

    setView("fill-params");
  };

  const handleBackToSelect = () => {
    setView("select");
    setSelectedTemplate(null);
    setParamValues({});
  };

  // Create form logic
  const detectedParams = useMemo(() => extractParams(bodyText), [bodyText]);

  const handleTemplateNameChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    const withValidStart = sanitized.replace(/^[^a-z]+/, "");
    setTemplateName(withValidStart);
  };

  const isCreateFormValid = useMemo(() => {
    if (!templateName || !bodyText.trim()) return false;
    if (!templateName.match(/^[a-z][a-z0-9_]*$/)) return false;
    return detectedParams.every((p) => (paramExamples[p] || "").trim().length > 0);
  }, [templateName, bodyText, detectedParams, paramExamples]);

  const handleCreateTemplate = () => {
    if (!isCreateFormValid || !whatsappConnectionId) return;

    createTemplateMutation.mutate(
      {
        merchantId,
        whatsappConnectionId,
        config: {
          name: templateName,
          category: "MARKETING" as const,
          ...(detectedParams.length > 0 ? { parameter_format: "NAMED" as const } : {}),
          language,
          body: { text: bodyText },
        },
        params: detectedParams.map((p) => ({ name: p, example: paramExamples[p] || "" })),
      },
      {
        onSuccess: () => {
          toast.success("Plantilla creada", {
            description: "Estará disponible cuando Meta la apruebe.",
            duration: 4000,
          });
          utils.whatsappTemplates.list.invalidate();
          setTemplateName("");
          setLanguage("es");
          setBodyText("");
          setParamExamples({});
          setView("select");
        },
        onError: (error) => {
          toast.error("Error al crear plantilla", {
            description: error.message,
            duration: 5000,
          });
        },
      }
    );
  };

  const resetCreateForm = () => {
    setTemplateName("");
    setLanguage("es");
    setBodyText("");
    setParamExamples({});
    setView("select");
  };

  const allParamsFilled = useMemo(() => {
    if (!selectedTemplate) return false;
    return selectedTemplate.params.every((param) => (paramValues[param] || "").trim().length > 0);
  }, [selectedTemplate, paramValues]);

  const handleSendTemplate = () => {
    if (!selectedTemplate || !allParamsFilled) return;

    const filledContent = selectedTemplate.bodyText.replace(
      /{{(\w+)}}/g,
      (_, key) => paramValues[key] || ""
    );

    const optimisticId = ulid();
    const optimisticMessage: OptimisticMessage = {
      type: "message",
      timestamp: new Date().toISOString(),
      message: {
        id: optimisticId,
        conversationId: "",
        content: filledContent,
        source: "human_agent",
        sourceId: userEmail,
        createdAt: new Date().toISOString(),
        lastStatus: "pending",
        isOptimistic: true,
      },
    };

    onOptimisticMessage(optimisticMessage);

    sendTemplateMutation.mutate(
      {
        merchantId,
        leadId,
        templateId: selectedTemplate.id,
        params: paramValues,
      },
      {
        onSuccess: (result) => {
          onRemoveOptimisticMessage(optimisticId);
          onTemplateSent();
          if (!result.success) {
            toast.warning("Error al enviar", { duration: 3000 });
          } else {
            toast.success("Plantilla enviada", { duration: 2000 });
          }
          setView("select");
          setSelectedTemplate(null);
          setParamValues({});
          setIsSheetOpen(false);
        },
        onError: (error) => {
          onMarkOptimisticFailed(optimisticId);
          toast.error("Error al enviar plantilla", {
            description: error.message,
            duration: 5000,
          });
        },
      }
    );
  };

  const getStatusBadge = (status: string) => {
    if (status === "APPROVED") return { color: "green", label: "Aprobada" };
    if (status === "REJECTED") return { color: "red", label: "Rechazada" };
    if (status === "DELETED") return { color: "red", label: "Eliminada" };
    return { color: "yellow", label: "Pendiente" };
  };

  const handleSheetClose = (open: boolean) => {
    setIsSheetOpen(open);
    if (!open) {
      setView("select");
      setSelectedTemplate(null);
      setParamValues({});
    }
  };

  // Build content based on current view
  let content: JSX.Element;

  if (view === "create") {
    content = (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Button size="sm" variant="ghost" onClick={resetCreateForm}>
            <ArrowLeft className="mr-1 size-4" />
            Volver
          </Button>
          {!isMobile && <p className="text-sm font-medium">Nueva plantilla</p>}
        </div>

        {!whatsappConnectionId && (
          <Alert status="warning">
            <p className="text-sm">
              No se pudo determinar la conexión de WhatsApp. No se puede crear la plantilla.
            </p>
          </Alert>
        )}

        <div>
          <p className="mb-1 text-xs font-medium text-gray-600">Nombre</p>
          <input
            className="h-8 w-full rounded-md border border-gray-200 bg-white px-3 text-base focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary md:text-sm"
            value={templateName}
            onChange={(e) => handleTemplateNameChange(e.target.value)}
            placeholder="nombre_de_la_plantilla"
          />
        </div>

        <div>
          <p className="mb-1 text-xs font-medium text-gray-600">Idioma</p>
          <select
            className="h-8 w-full rounded-md border border-gray-200 bg-white px-3 text-base focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary md:text-sm"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="es">Español</option>
            <option value="en">English</option>
            <option value="pt">Português</option>
          </select>
        </div>

        <div>
          <p className="mb-1 text-xs font-medium text-gray-600">Cuerpo del mensaje</p>
          <textarea
            className="min-h-[80px] max-h-[120px] w-full resize-none rounded-md border border-gray-200 bg-white px-3 py-2 text-base focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary md:text-sm"
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            placeholder="Escribe el mensaje. Usa {{nombre_param}} para parámetros dinámicos"
          />
        </div>

        {detectedParams.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium text-gray-600">
              Ejemplos de parámetros (requerido por Meta)
            </p>
            <div className="flex flex-col gap-2">
              {detectedParams.map((param) => (
                <div key={param}>
                  <p className="mb-1 text-[10px] text-gray-500">{`{{${param}}}`}</p>
                  <input
                    className="h-8 w-full rounded-md border border-gray-200 bg-white px-3 text-base focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary md:text-sm"
                    value={paramExamples[param] || ""}
                    onChange={(e) =>
                      setParamExamples((prev) => ({ ...prev, [param]: e.target.value }))
                    }
                    placeholder={`Ejemplo para {{${param}}}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <Button
          size="sm"
          onClick={handleCreateTemplate}
          disabled={!isCreateFormValid || !whatsappConnectionId || createTemplateMutation.isLoading}
          className="rounded-full"
        >
          {createTemplateMutation.isLoading ? (
            <>
              <Spinner size="sm" className="mr-1" />
              Creando
            </>
          ) : (
            "Crear plantilla"
          )}
        </Button>
      </div>
    );
  } else if (view === "fill-params" && selectedTemplate) {
    content = (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Button size="sm" variant="ghost" onClick={handleBackToSelect}>
            <ArrowLeft className="mr-1 size-4" />
            Volver
          </Button>
          <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
            {selectedTemplate.name}
          </span>
        </div>

        <div className="rounded-md bg-gray-50 p-3 text-sm">
          {renderBodyWithHighlightedParams(selectedTemplate.bodyText, paramValues)}
        </div>

        {selectedTemplate.params.map((param) => (
          <div key={param}>
            <p className="mb-1 text-xs font-medium text-gray-600">{param}</p>
            <input
              className="h-8 w-full rounded-md border border-gray-200 bg-white px-3 text-base focus:border-palomma-primary focus:outline-none focus:ring-1 focus:ring-palomma-primary md:text-sm"
              value={paramValues[param] || ""}
              onChange={(e) =>
                setParamValues((prev) => ({ ...prev, [param]: e.target.value }))
              }
              placeholder={`Valor para {{${param}}}`}
            />
          </div>
        ))}

        <Button
          size="sm"
          onClick={handleSendTemplate}
          disabled={!allParamsFilled || sendTemplateMutation.isLoading}
          className="rounded-full bg-green-600 hover:bg-green-700"
        >
          {sendTemplateMutation.isLoading ? (
            <>
              <Spinner size="sm" className="mr-1" />
              Enviando
            </>
          ) : (
            <>
              <Send className="mr-1 size-4" />
              Enviar plantilla
            </>
          )}
        </Button>
      </div>
    );
  } else {
    // Select view
    content = (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-7 border-green-500 text-green-600 hover:bg-green-50"
              onClick={() => syncStatusesMutation.mutate({ merchantId })}
              disabled={syncStatusesMutation.isLoading}
            >
              {syncStatusesMutation.isLoading ? (
                <>
                  <Spinner size="sm" className="mr-1" />
                  Sincronizando
                </>
              ) : (
                <>
                  <RefreshCw className="mr-1 size-4" />
                  Sincronizar
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => setView("create")}
            >
              <Plus className="mr-1 size-4" />
              Crear plantilla
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Spinner size="sm" />
          </div>
        ) : !wabaId ? (
          <p className="py-4 text-center text-sm text-gray-500">
            No se pudo determinar la conexión de WhatsApp. No se pueden cargar plantillas.
          </p>
        ) : templates.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">
            No hay plantillas disponibles para esta conexión de WhatsApp.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
              {approvedTemplates.map((template) => {
                const badge = getStatusBadge(template.status);
                return (
                  <div
                    key={template.id}
                    className="cursor-pointer rounded-md border border-gray-200 p-3 transition-all duration-150 hover:border-purple-300 hover:bg-purple-50"
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <p className="truncate text-sm font-medium">{template.name}</p>
                      <div className="flex items-center gap-1">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            badge.color === "green"
                              ? "bg-green-100 text-green-800"
                              : badge.color === "red"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {badge.label}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-800">
                          {template.language.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <p className="line-clamp-2 text-xs text-gray-500">
                      {template.bodyPreview || ""}
                    </p>
                  </div>
                );
              })}

              {nonApprovedTemplates.map((template) => {
                const badge = getStatusBadge(template.status);
                return (
                  <div
                    key={template.id}
                    className="cursor-not-allowed rounded-md border border-gray-100 p-3 opacity-60"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <p className="truncate text-sm font-medium">{template.name}</p>
                      <div className="flex items-center gap-1">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            badge.color === "green"
                              ? "bg-green-100 text-green-800"
                              : badge.color === "red"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {badge.label}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-800">
                          {template.language.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <p className="line-clamp-2 text-xs text-gray-500">
                      {template.bodyPreview || ""}
                    </p>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  }

  // Compact trigger bar + Sheet for all viewports
  return (
    <>
      <div className="sticky bottom-0 border-t border-gray-200 bg-white px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <Info className="size-3.5 shrink-0 text-gray-400" />
            <span className="truncate text-xs text-gray-500">
              Solo plantillas — el cliente debe responder para mensajes libres.
            </span>
          </div>
          <Button
            size="sm"
            className="shrink-0 rounded-full bg-green-600 text-white hover:bg-green-700"
            onClick={() => setIsSheetOpen(true)}
          >
            <Send className="size-3.5" />
            Plantilla
          </Button>
        </div>
      </div>
      <Sheet open={isSheetOpen} onOpenChange={handleSheetClose}>
        <SheetContent
          side={isMobile ? "bottom" : "right"}
          className={cn(
            "overflow-y-auto p-4",
            isMobile ? "max-h-[85dvh] rounded-t-2xl" : "w-[420px] sm:w-[420px]"
          )}
        >
          <SheetHeader className="mb-3 pr-8">
            <SheetTitle className="text-left text-base">
              {view === "create"
                ? "Nueva plantilla"
                : view === "fill-params"
                  ? "Enviar plantilla"
                  : "Plantillas disponibles"}
            </SheetTitle>
          </SheetHeader>
          {content}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default TemplateMessageFlow;
